import React from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box } from '@chakra-ui/react'

import {
  FacilityOptionLabel,
  FormInputControl,
  FormInputSelectControl,
} from '@/components/ui'
import { DeviceType } from '@/graphql/generated/schemas'
import { createFacilityAddressFilter } from '@/utils/forms/optionBuilders'
import {
  getFacilityNoOptionsMessage,
  maxLength,
} from '@/utils/forms/validation'

import { Options } from '../../types/types'
import { FormInputsIProps } from '../types/types'

interface GenericDeviceFormIProps {
  form: UseFormMethods<FormInputsIProps>
  facilityOptions: Options
  floorOptions: Options
  isOptionsLoading: boolean
  isFacilitiesLoading: boolean
  isFacilitiesError: boolean
  isIncidentTypesLoading: boolean
  incidentTypeOptions: Options
  setFacilityName?: React.Dispatch<React.SetStateAction<string>>
}

export const GenericDeviceForm = ({
  form,
  isOptionsLoading,
  isFacilitiesLoading,
  isFacilitiesError,
  facilityOptions,
  floorOptions,
  isIncidentTypesLoading,
  incidentTypeOptions,
  setFacilityName,
}: GenericDeviceFormIProps) => {
  const { t } = useTranslation()
  const { control, errors, register, watch } = form
  const isEventPromotion = useFeatureFlag(FeatureFlag.useEventPromotionAlarms)
  const type = watch('type')
  return (
    <Box>
      <Box mb='3'>
        <FormInputControl
          data-testid='devicesPage_genericModal_name'
          errorMessage={errors.name && errors.name.message}
          id='name'
          inputRef={register({
            maxLength: maxLength(255),
            required: 'Device Name is required',
          })}
          isInvalid={!!errors.name}
          label='Device Name'
          placeholder='Enter the Device Name'
        />
      </Box>
      <Box mb='3'>
        <FormInputSelectControl
          control={control}
          data-testid='devicesPage_genericModal_facility'
          defaultValue={null}
          errorMessage='Select a facility'
          filterOption={createFacilityAddressFilter}
          formatOptionLabel={(option, meta) => (
            <FacilityOptionLabel
              isValueContext={meta?.context === 'value'}
              option={option}
            />
          )}
          id='facility'
          isClearable
          isDisabled={isOptionsLoading}
          isInvalid={!!errors?.facility}
          isLoading={isFacilitiesLoading}
          label='Facility'
          noOptionsMessage={({ inputValue }) =>
            getFacilityNoOptionsMessage(
              inputValue,
              isFacilitiesError,
              t('common:facility_search_error')
            )
          }
          onInputChange={setFacilityName}
          options={facilityOptions}
          placeholder='Select Device Facility'
          rules={{ required: true }}
        />
      </Box>

      <Box mb='3'>
        <FormInputSelectControl
          control={control}
          data-testid='devicesPage_genericModal_floor'
          defaultValue={null}
          errorMessage='Select a floor'
          id='floor'
          isClearable
          isDisabled={isOptionsLoading || floorOptions.length === 0}
          isInvalid={!!errors?.floor}
          label='Floor'
          options={floorOptions}
          placeholder='Select Device Floor'
          rules={{ required: true }}
        />
      </Box>
      {isEventPromotion && type?.value === DeviceType.Alarm && (
        <Box mb='3'>
          <FormInputSelectControl
            control={control}
            data-testid='devicesPage_genericModal_alarmTypeId'
            defaultValue={null}
            id='alarmTypeId'
            isClearable
            isDisabled={
              isIncidentTypesLoading || incidentTypeOptions.length === 0
            }
            isLoading={isIncidentTypesLoading}
            label='Incident Type'
            options={incidentTypeOptions}
            placeholder='Select Incident Type'
          />
        </Box>
      )}
    </Box>
  )
}
